/**
 * Append-only audit log writer.
 *
 * Schema: supabase/migrations/00307_audit_log.sql.
 *
 * Phase 1 producer is the SOP engine — every evaluation that produces a
 * finding posts here alongside the sop_evaluations row + notification. The
 * two stores complement each other: sop_evaluations is keyed by the SOP +
 * entity (the cockpit reads it per-entity); audit_log is keyed by time and
 * actor (a compliance reader walks it as a timeline).
 *
 * Failures here are swallowed at the caller (engine logs and continues) so
 * a transient audit problem doesn't disrupt the mutation that triggered it.
 */

import { supabase } from '@/lib/supabase'

export type AuditSeverity = 'info' | 'warn' | 'block' | 'deny'

export interface AuditLogEntry {
  id: string
  kind: string
  entityType: string | null
  entityId: string | null
  actor: string | null
  actorName: string | null
  action: string
  summary: string
  payload: Record<string, unknown>
  severity: AuditSeverity
  portfolioId: string | null
  createdAt: string
}

export interface AuditWrite {
  kind: string
  entityType?: string | null
  entityId?: string | null
  actor?: string | null
  actorName?: string | null
  action: string
  summary: string
  payload?: Record<string, unknown>
  severity?: AuditSeverity
  portfolioId?: string | null
}

function rowToEntry(row: Record<string, unknown>): AuditLogEntry {
  return {
    id: String(row.id),
    kind: String(row.kind),
    entityType: (row.entity_type as string | null) ?? null,
    entityId: (row.entity_id as string | null) ?? null,
    actor: (row.actor as string | null) ?? null,
    actorName: (row.actor_name as string | null) ?? null,
    action: String(row.action),
    summary: String(row.summary),
    payload: ((row.payload as Record<string, unknown> | null) ?? {}),
    severity: (row.severity as AuditSeverity) ?? 'info',
    portfolioId: (row.portfolio_id as string | null) ?? null,
    createdAt: String(row.created_at),
  }
}

export async function writeAuditEntry(input: AuditWrite): Promise<void> {
  const { error } = await supabase.from('audit_log').insert({
    kind: input.kind,
    entity_type: input.entityType ?? null,
    entity_id: input.entityId ?? null,
    actor: input.actor ?? null,
    actor_name: input.actorName ?? null,
    action: input.action,
    summary: input.summary.slice(0, 500),
    payload: input.payload ?? {},
    severity: input.severity ?? 'info',
    portfolio_id: input.portfolioId ?? null,
  })
  if (error) throw new Error(`audit_log insert failed: ${error.message}`)
}

export async function listRecentAudit(limit = 200): Promise<AuditLogEntry[]> {
  const { data, error } = await supabase
    .from('audit_log')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return (data ?? []).map((r) => rowToEntry(r as Record<string, unknown>))
}

export async function listAuditForEntity(
  entityType: string,
  entityId: string,
  limit = 100,
): Promise<AuditLogEntry[]> {
  const { data, error } = await supabase
    .from('audit_log')
    .select('*')
    .eq('entity_type', entityType)
    .eq('entity_id', entityId)
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return (data ?? []).map((r) => rowToEntry(r as Record<string, unknown>))
}
